import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { BACKEND_URL } from '../../config/canonical';
import { Plus, Car, Trash2, Eye, Pencil } from 'lucide-react';

const VehiclesTab = ({ 
  caseId, 
  vehicles, 
  canEdit, 
  isAdmin, 
  token, 
  onRefresh,
  onViewItem,
  onEditItem,
  onDeleteItem
}) => {
  const handleDeleteVehicle = async (vehicleId) => {
    if (onDeleteItem) return onDeleteItem('vehicle', vehicleId, 'Delete this vehicle?');
    if (!window.confirm('Delete this vehicle?')) return;
    await fetch(`${BACKEND_URL}/api/vehicles/${vehicleId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    onRefresh();
  };

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex justify-end">
          <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white" onClick={() => window.location.assign(`/cases/${caseId}/vehicles/new`)} data-testid="add-vehicle-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Vehicle
          </Button>
        </div>
      )}
      {vehicles.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-8 text-center">
            <Car className="w-12 h-12 mx-auto text-slate-600 mb-2" />
            <p className="text-slate-500">No vehicles tracked yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {vehicles.map(vehicle => (
            <Card key={vehicle.id} className="bg-white border-slate-200 hover:border-[#0F172A]/30 transition-colors" data-testid={`vehicle-card-${vehicle.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2 gap-2">
                  <button onClick={() => onViewItem(vehicle, 'vehicle')} className="text-left flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-slate-900 font-mono font-bold">{vehicle.license_plate}</span>
                      {vehicle.state && <Badge variant="outline" className="border-slate-300 text-slate-600 text-xs">{vehicle.state}</Badge>}
                    </div>
                    <p className="text-slate-800">{[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}</p>
                  </button>
                </div>
                <div className="space-y-1 text-sm">
                  {vehicle.color && <div className="flex gap-2"><span className="text-slate-500">Color:</span><span className="text-slate-800">{vehicle.color}</span></div>}
                  {vehicle.registered_owner && <div className="flex gap-2"><span className="text-slate-500">Owner:</span><span className="text-slate-800">{vehicle.registered_owner}</span></div>}
                  {vehicle.notes && <p className="text-slate-500 text-xs mt-2">{vehicle.notes}</p>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100"
                    onClick={() => onViewItem(vehicle, 'vehicle')} data-testid={`vehicle-view-${vehicle.id}`} title="View details">
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                  {canEdit && (
                    <Button size="sm" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100"
                      onClick={() => (onEditItem ? onEditItem(vehicle, 'vehicle') : onViewItem(vehicle, 'vehicle'))}
                      data-testid={`vehicle-edit-${vehicle.id}`} title="Edit vehicle">
                      <Pencil className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteVehicle(vehicle.id)} data-testid={`vehicle-delete-${vehicle.id}`} title="Delete vehicle">
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Vehicle dialog removed — "Add Vehicle" button routes to
          /cases/:caseId/vehicles/new (VehicleFormPage). View / Edit rows
          route via parent onViewItem/onEditItem handlers. */}
    </div>
  );
};

export default VehiclesTab;
